import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { Heading1 } from "../../../common/components/styled/styled";
import {
  createEezyCompanyOrderQueue,
  createOrderQueue,
} from "../settingsActions";
import { OrderQueueForm } from "./OrderQueueForm";
import { OrderQueueEvent, OrderQueueType } from "./orderQueueApi";
import { OrderQueueOption } from "./types";

interface Props {
  isOpen: boolean;
  close: () => void;
  updateQueueList: (newQueue: OrderQueueOption) => void;
}

type OrderQueueFormValues = {
  name: string;
  type: OrderQueueType;
  cities: { id: string; name: string }[];
  eezyCompany: { eezyCompanyId: string; name: string };
  members: { id: string; name: string }[];
};

const AddOrderQueue = ({ isOpen, close, updateQueueList }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const defaultValues: OrderQueueFormValues = {
    name: "",
    type: OrderQueueType.CITY,
    cities: [],
    eezyCompany: { eezyCompanyId: "", name: "" },
    members: [],
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleQueueCreated = (queueEvents: OrderQueueEvent[]) => {
    // At this point queue needs minimal information to be displayed in the list
    const createdEvent = queueEvents.find((event) =>
      ["ordQueueNew", "ecmpOrdQueueNew", "cntOrdQueueNew"].includes(event.name),
    )!!;
    const queue = {
      ...createdEvent.payload,
      id: createdEvent.id,
    };
    updateQueueList(queue);
    close();
    methods.reset();
  };

  const createQueue = () => {
    const form = methods.getValues();
    setIsLoading(true);
    switch (form.type) {
      case OrderQueueType.CITY:
        dispatch(
          createOrderQueue({
            name: form.name,
            cities: form.cities.map((city) => city.id),
            experts: form.members.map((member) => member.id),
          }),
        ).then((action) => {
          setIsLoading(false);
          if (isFulfilled(action)) handleQueueCreated(action.payload);
        });
        break;
      case OrderQueueType.TEAM_SPECIFIC:
        dispatch(
          createOrderQueue({
            name: form.name,
            experts: form.members.map((member) => member.id),
          }),
        ).then((action) => {
          setIsLoading(false);
          if (isFulfilled(action)) handleQueueCreated(action.payload);
        });
        break;
      case OrderQueueType.EC:
        dispatch(
          createEezyCompanyOrderQueue({
            name: form.name,
            eezyCompanyId: form.eezyCompany.eezyCompanyId,
            cities: form.cities.map((city) => city.id),
            experts: form.members.map((member) => member.id),
          }),
        ).then((action) => {
          setIsLoading(false);
          if (isFulfilled(action)) handleQueueCreated(action.payload);
        });
        break;
      default:
        break;
    }
  };

  return (
    <DialogComponent open={isOpen} onClose={close}>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Heading1>{t("orderQueues:addQueue")}</Heading1>
          <IconButton
            onClick={() => {
              if (isLoading) return;
              close();
              methods.reset();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <FormProvider {...methods}>
          <OrderQueueForm
            isNewQueue
            onSubmit={createQueue}
            isLoading={isLoading}
          />
        </FormProvider>
      </Box>
    </DialogComponent>
  );
};

export default AddOrderQueue;
